import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import './DineInPage.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:9001';
const PETPOOJA_API = `${API_BASE}/petpooja`;

/* ── helpers ──────────────────────────────────────────── */
function rupee(val) {
  return `₹${parseFloat(val || 0).toFixed(0)}`;
}

function isActive(val) {
  return String(val) === '1';
}

function inStock(val) {
  // instock: "2" = in stock, "1" = out of stock (Petpooja convention)
  return String(val) !== '1';
}

function vegDot(attrId) {
  // attribute_id: 1 = veg, 2 = non-veg, 5 = other, 24 = egg
  const s = String(attrId || '').toLowerCase();
  if (s === '24' || s === 'egg') return 'egg';
  if (s === '2' || s === 'non_veg' || s === 'non-veg') return 'non-veg';
  if (s === '5' || s === 'other') return 'other';
  return 'veg';
}

/* ── Checkmark SVG ────────────────────────────────────── */
const CheckIcon = () => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
    <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ── Item-level cart key helpers ──────────────────────── */
function makeCartKey(item, variation = null) {
  return variation ? `${item.itemid}_var_${variation.id}` : `${item.itemid}_simple`;
}

/* ═══════════════════════════════════════════════════════════
   DRAWER: Variation + Addon + Note picker
════════════════════════════════════════════════════════════ */
function ItemDrawer({ item, addongroups, onClose, onAddToCart }) {
  const hasVariations = String(item.itemallowvariation) === '1' && item.variation?.length > 0;
  const [selectedVariation, setSelectedVariation] = useState(
    hasVariations ? null : null
  );
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [note, setNote] = useState('');
  const [qty, setQty] = useState(1);

  // Determine which addon groups apply
  const applicableAddonGroupIds = useCallback(() => {
    if (hasVariations && selectedVariation) {
      return (selectedVariation.addon || []).map((a) => a.addon_group_id);
    }
    return (item.addon || []).map((a) => a.addon_group_id);
  }, [hasVariations, selectedVariation, item.addon]);

  const applicableGroups = addongroups.filter((g) =>
    applicableAddonGroupIds().includes(g.addongroupid)
  );

  function toggleAddon(groupId, groupName, addonItem) {
    const key = `${groupId}_${addonItem.addonitemid}`;
    const newAddons = selectedAddons.filter(
      (a) => `${a.group_id}_${a.id}` !== key
    );
    if (newAddons.length === selectedAddons.length) {
      // not yet selected — add it
      newAddons.push({
        group_id: groupId,
        group_name: groupName,
        id: addonItem.addonitemid,
        name: addonItem.addonitem_name,
        price: addonItem.addonitem_price,
        quantity: 1,
      });
    }
    setSelectedAddons(newAddons);
  }

  function isAddonSelected(groupId, addonId) {
    return selectedAddons.some((a) => a.group_id === groupId && a.id === addonId);
  }

  function computeBasePrice() {
    if (hasVariations && selectedVariation) {
      return parseFloat(selectedVariation.price || 0);
    }
    return parseFloat(item.price || 0);
  }

  function computeTotalPrice() {
    const base = computeBasePrice();
    const addonTotal = selectedAddons.reduce(
      (sum, a) => sum + parseFloat(a.price || 0) * (a.quantity || 1),
      0
    );
    return (base + addonTotal) * qty;
  }

  function handleAdd() {
    const cartKey = makeCartKey(item, hasVariations ? selectedVariation : null);
    const itemId = hasVariations && selectedVariation
      ? selectedVariation.id
      : item.itemid;

    // Build name: "ItemName (VariationName)" format
    const itemName = hasVariations && selectedVariation
      ? `${item.itemname} (${selectedVariation.name})`
      : item.itemname;

    const addonStr = selectedAddons.length > 0
      ? JSON.stringify(selectedAddons)
      : '';

    const addonLabel = selectedAddons.map((a) => a.name).join(', ');
    const variationLabel = hasVariations && selectedVariation
      ? selectedVariation.name
      : null;

    onAddToCart({
      cartKey,
      itemId: String(itemId),
      itemName,        // formatted name for KOT payload
      displayName: item.itemname,
      variationName: variationLabel,
      addonLabel,
      addonStr,
      note,
      qty,
      unitPrice: computeBasePrice(),
      addonPrice: selectedAddons.reduce(
        (s, a) => s + parseFloat(a.price || 0) * (a.quantity || 1),
        0
      ),
      totalPrice: computeTotalPrice(),
    });
    onClose();
  }

  const canAdd = !hasVariations || selectedVariation !== null;

  return (
    <>
      <div className="di-overlay" onClick={onClose} />
      <div className="di-drawer" role="dialog" aria-modal="true">
        <div className="di-drawer-handle" />
        <div className="di-drawer-head">
          <h2>{item.itemname}</h2>
          <button className="di-drawer-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="di-drawer-body">
          {/* Item description */}
          {item.itemdescription && (
            <p style={{ fontSize: 13, color: 'var(--di-muted)', marginBottom: 16, lineHeight: 1.5 }}>
              {item.itemdescription}
            </p>
          )}

          {/* Variations */}
          {hasVariations && (
            <>
              <div className="di-label-pill">Choose Size / Variant</div>
              <div className="di-variations-grid">
                {(item.variation || []).filter(v => isActive(v.active)).slice().sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0)).map((v) => (
                  <div
                    key={v.id}
                    className={`di-var-card ${selectedVariation?.id === v.id ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedVariation(v);
                      setSelectedAddons([]); // reset addons when variation changes
                    }}
                  >
                    <span className="di-var-card-name">{v.name}</span>
                    <span className="di-var-card-price">{rupee(v.price)}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Addons */}
          {applicableGroups.length > 0 && (
            <>
              <div className="di-label-pill" style={{ marginTop: 20 }}>Customise</div>
              {applicableGroups.map((group) => {
                const groupConfig = (
                  hasVariations && selectedVariation
                    ? selectedVariation.addon
                    : item.addon
                ).find((a) => a.addon_group_id === group.addongroupid) || {};

                const max = parseInt(groupConfig.addon_item_selection_max || 99);
                const min = parseInt(groupConfig.addon_item_selection_min || 0);

                return (
                  <div key={group.addongroupid} className="di-addon-group">
                    <div className="di-addon-group-name">{group.addongroup_name}</div>
                    <div className="di-addon-group-meta">
                      {min > 0 ? `Required · ` : 'Optional · '}
                      {max === 99 ? 'Select any' : `Select up to ${max}`}
                    </div>
                    {group.addongroupitems.filter(ai => isActive(ai.active)).map((ai) => {
                      const selected = isAddonSelected(group.addongroupid, ai.addonitemid);
                      return (
                        <div
                          key={ai.addonitemid}
                          className={`di-addon-item ${selected ? 'selected' : ''}`}
                          onClick={() => toggleAddon(group.addongroupid, group.addongroup_name, ai)}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                            <div className="di-addon-check">
                              <CheckIcon />
                            </div>
                            <span className="di-addon-item-name">{ai.addonitem_name}</span>
                          </div>
                          {parseFloat(ai.addonitem_price) > 0 && (
                            <span className="di-addon-item-price">+{rupee(ai.addonitem_price)}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </>
          )}

          {/* Special note */}
          <div className="di-label-pill" style={{ marginTop: applicableGroups.length > 0 ? 8 : 20 }}>
            Special Instructions
          </div>
          <textarea
            className="di-special-note"
            rows={2}
            placeholder="e.g. Less spicy, no onion…"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          {/* Qty selector */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Quantity</span>
            <div className="di-counter">
              <button onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
              <span>{qty}</span>
              <button onClick={() => setQty(q => q + 1)}>+</button>
            </div>
          </div>
        </div>
        <div className="di-drawer-footer">
          <button
            className="di-btn-primary"
            onClick={handleAdd}
            disabled={!canAdd}
            id="di-add-to-cart-btn"
          >
            {canAdd
              ? `Add to Order · ${rupee(computeTotalPrice())}`
              : 'Select a variant to continue'}
          </button>
        </div>
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   DRAWER: Cart Review
════════════════════════════════════════════════════════════ */
function CartDrawer({ cart, onClose, onPlaceOrder, placing }) {
  const [tableNote, setTableNote] = useState('');
  const cartItems = Object.values(cart);
  const subtotal = cartItems.reduce((s, i) => s + i.totalPrice, 0);

  return (
    <>
      <div className="di-overlay" onClick={onClose} />
      <div className="di-drawer" role="dialog" aria-modal="true">
        <div className="di-drawer-handle" />
        <div className="di-drawer-head">
          <h2>Your Order</h2>
          <button className="di-drawer-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="di-drawer-body">
          {cartItems.length === 0 ? (
            <div className="di-cart-empty">
              <div className="di-cart-empty-icon">🛒</div>
              <p>Your order is empty. Add some items first!</p>
            </div>
          ) : (
            <>
              {cartItems.map((ci) => (
                <div key={ci.cartKey} className="di-cart-item-row">
                  <div className="di-cart-item-info">
                    <div className="di-cart-item-name">{ci.displayName}</div>
                    <div className="di-cart-item-sub">
                      {ci.variationName && <span>{ci.variationName} · </span>}
                      {ci.addonLabel && <span>{ci.addonLabel} · </span>}
                      {ci.note && <span>"{ci.note}"</span>}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                      <div className="di-counter" style={{ transform: 'scale(.85)', transformOrigin: 'left' }}>
                        <button onClick={() => onPlaceOrder('decrement', ci)}>−</button>
                        <span>{ci.qty}</span>
                        <button onClick={() => onPlaceOrder('increment', ci)}>+</button>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                    <div className="di-cart-item-price">{rupee(ci.totalPrice)}</div>
                    <button
                      onClick={() => onPlaceOrder('remove', ci)}
                      style={{ background: 'none', border: 'none', color: 'var(--di-muted)', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}
                      aria-label="Remove item"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}

              <div className="di-cart-summary" style={{ marginTop: 16 }}>
                <div className="di-cart-summary-row">
                  <span>{cartItems.reduce((s, i) => s + i.qty, 0)} items</span>
                  <span>{rupee(subtotal)}</span>
                </div>
                <div className="di-cart-summary-row total">
                  <span>Total</span>
                  <span>{rupee(subtotal)}</span>
                </div>
              </div>
            </>
          )}
        </div>
        {cartItems.length > 0 && (
          <div className="di-drawer-footer">
            <button
              className="di-btn-primary"
              disabled={placing}
              onClick={() => onPlaceOrder('submit')}
              id="di-place-order-btn"
            >
              {placing ? 'Placing Order…' : `Place Order · ${rupee(subtotal)}`}
            </button>
          </div>
        )}
      </div>
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
════════════════════════════════════════════════════════════ */
export default function DineInPage() {
  const { business_id, mapping_id } = useParams();

  const [menuData, setMenuData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Category navigation
  const [activeCatId, setActiveCatId] = useState(null);

  // Drawers
  const [itemDrawer, setItemDrawer] = useState(null); // item object or null
  const [cartOpen, setCartOpen] = useState(false);

  // Cart: { [cartKey]: cartItem }
  const [cart, setCart] = useState({});

  // Order flow
  const [placing, setPlacing] = useState(false);
  const [orderResult, setOrderResult] = useState(null); // null | { success, kotId, clientKotId }
  const [orderError, setOrderError] = useState(null);

  const sectionRefs = useRef({});

  /* ── Fetch menu ──────────────────────────────────────── */
  useEffect(() => {
    const fetchMenu = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${PETPOOJA_API}/fetch_dinein_menu?mapping_id=${mapping_id}&business_id=${business_id}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'x-business-id': business_id,
            },
          }
        );
        const rawData = await res.json();
        
        // Handle wrapped API responses (e.g. { success: true, data: { ...actualMenuData... } })
        let data = rawData;
        if (rawData.success === true && rawData.data) {
          data = typeof rawData.data === 'string' ? JSON.parse(rawData.data) : rawData.data;
          // Petpooja API might also wrap its own responses in data.response
          if (data.response && typeof data.response === 'string') {
            data = JSON.parse(data.response);
          } else if (data.response && typeof data.response === 'object') {
            data = data.response;
          }
        }

        if (data.success === '1' || data.success === true || rawData.success === true) {
          setMenuData(data);
          // Set first active category as default
          const firstCat = (data.categories || []).find(c => isActive(c.active));
          if (firstCat) setActiveCatId(firstCat.categoryid);
        } else {
          setError(data.message || rawData.message || 'Failed to load menu.');
        }
      } catch (e) {
        setError('Network error. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchMenu();
  }, [business_id, mapping_id]);

  /* ── Derived data ────────────────────────────────────── */
  const restaurant = menuData?.restaurants?.[0]?.details || {};
  const categories = (menuData?.categories || []).filter(c => isActive(c.active));
  const addongroups = menuData?.addongroups || [];

  function itemsForCategory(categoryId) {
    return (menuData?.items || []).filter(
      (it) => it.item_categoryid === categoryId && isActive(it.active)
    );
  }

  /* ── Category tab scroll ─────────────────────────────── */
  function scrollToCategory(catId) {
    setActiveCatId(catId);
    const ref = sectionRefs.current[catId];
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /* ── Cart helpers ────────────────────────────────────── */
  function addToCart(cartItem) {
    setCart(prev => {
      const existing = prev[cartItem.cartKey];
      if (existing) {
        const newQty = existing.qty + cartItem.qty;
        const unitTotal = (existing.unitPrice + existing.addonPrice) * newQty;
        return {
          ...prev,
          [cartItem.cartKey]: { ...existing, qty: newQty, totalPrice: unitTotal },
        };
      }
      return { ...prev, [cartItem.cartKey]: cartItem };
    });
  }

  function cartQtyForKey(cartKey) {
    return cart[cartKey]?.qty || 0;
  }

  function totalCartItems() {
    return Object.values(cart).reduce((s, i) => s + i.qty, 0);
  }

  function handleCartAction(action, ci) {
    if (action === 'remove') {
      setCart(prev => {
        const updated = { ...prev };
        delete updated[ci.cartKey];
        return updated;
      });
    } else if (action === 'decrement') {
      setCart(prev => {
        const existing = prev[ci.cartKey];
        if (!existing) return prev;
        const newQty = existing.qty - 1;
        if (newQty <= 0) {
          const updated = { ...prev };
          delete updated[ci.cartKey];
          return updated;
        }
        return {
          ...prev,
          [ci.cartKey]: {
            ...existing,
            qty: newQty,
            totalPrice: (existing.unitPrice + existing.addonPrice) * newQty,
          },
        };
      });
    } else if (action === 'increment') {
      setCart(prev => {
        const existing = prev[ci.cartKey];
        if (!existing) return prev;
        const newQty = existing.qty + 1;
        return {
          ...prev,
          [ci.cartKey]: {
            ...existing,
            qty: newQty,
            totalPrice: (existing.unitPrice + existing.addonPrice) * newQty,
          },
        };
      });
    } else if (action === 'submit') {
      handlePlaceOrder();
    }
  }

  /* ── Place Order ─────────────────────────────────────── */
  async function handlePlaceOrder() {
    setPlacing(true);
    setOrderError(null);

    const kotItems = Object.values(cart).map((ci) => ({
      id: ci.itemId,
      name: ci.itemName,
      quantity: ci.qty,
      specialnotes: ci.note || '',
      AddonItem: ci.addonStr || '',
      fixed_per: '0',
    }));

    const payload = {
      mapping_id: parseInt(mapping_id),
      business_id: parseInt(business_id),
      kot_items: kotItems,
    };

    try {
      const res = await fetch(`${PETPOOJA_API}/save_kot_qr_order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-business-id': business_id,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (data.success === true || data.success === '1') {
        const inner = typeof data.data?.response === 'string'
          ? JSON.parse(data.data.response)
          : data.data?.response || {};
        setOrderResult({
          kotId: inner.kotID || inner.clientKotID || '—',
          clientKotId: inner.clientKotID || '—',
        });
        setCart({});
        setCartOpen(false);
      } else {
        setOrderError(data.message || 'Order could not be placed. Please try again.');
      }
    } catch (e) {
      setOrderError('Network error. Please check your connection.');
    } finally {
      setPlacing(false);
    }
  }

  /* ══ Render: Loading ══ */
  if (loading) {
    return (
      <div className="di-page">
        <div className="di-loading-state">
          <div className="di-spinner" />
          <p className="di-loading-text">Loading menu…</p>
        </div>
      </div>
    );
  }

  /* ══ Render: Error ══ */
  if (error) {
    return (
      <div className="di-page">
        <div className="di-error-state">
          <div className="di-error-icon">🍽️</div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Menu unavailable</h2>
          <p className="di-error-msg">{error}</p>
          <button className="di-btn-primary" style={{ marginTop: 8, maxWidth: 220, width: '100%' }} onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  /* ══ Render: Success Screen ══ */
  if (orderResult) {
    return (
      <div className="di-page">
        <div className="di-success-state">
          <div className="di-success-anim">✓</div>
          <h2 className="di-success-title">Order Placed!</h2>
          <p className="di-success-subtitle">
            Your order has been sent to the kitchen.<br />Sit back and relax 🎉
          </p>
          <div className="di-kot-id-badge">
            Order Reference
            <span>#{orderResult.kotId}</span>
          </div>
          {orderResult.clientKotId !== orderResult.kotId && (
            <div className="di-kot-id-badge">
              KOT ID
              <span>{orderResult.clientKotId}</span>
            </div>
          )}
          <button
            className="di-place-more-btn"
            onClick={() => setOrderResult(null)}
            id="di-order-more-btn"
          >
            Order More Items
          </button>
        </div>
      </div>
    );
  }

  /* ══ Render: Main Menu ══ */
  const totalItems = totalCartItems();

  return (
    <div className="di-page">
      {/* ── Header ── */}
      <header className="di-header">
        <div className="di-header-brand">
          <div className="di-header-logo">🍴</div>
          <div className="di-header-info">
            <h1>{restaurant.restaurantname || 'Dine In'}</h1>
            <p>
              {restaurant.city ? `${restaurant.city}` : 'Table Ordering'}
              {restaurant.contact ? ` · ${restaurant.contact}` : ''}
            </p>
          </div>
        </div>
        <button
          className="di-cart-fab"
          onClick={() => setCartOpen(true)}
          id="di-view-cart-btn"
          style={{ opacity: totalItems === 0 ? .6 : 1 }}
        >
          🛒
          {totalItems > 0 && (
            <div className="di-cart-badge">{totalItems}</div>
          )}
          {totalItems > 0 && <span>View Cart</span>}
        </button>
      </header>

      {/* ── Category Tabs ── */}
      {categories.length > 0 && (
        <nav className="di-category-bar" aria-label="Menu categories">
          {categories.map((cat) => (
            <button
              key={cat.categoryid}
              className={`di-cat-tab ${activeCatId === cat.categoryid ? 'active' : ''}`}
              onClick={() => scrollToCategory(cat.categoryid)}
              id={`di-cat-tab-${cat.categoryid}`}
            >
              {cat.categoryname}
            </button>
          ))}
        </nav>
      )}

      {/* ── Menu Items ── */}
      <main className="di-body">
        {categories.map((cat) => {
          const items = itemsForCategory(cat.categoryid);
          if (items.length === 0) return null;
          return (
            <div
              key={cat.categoryid}
              ref={(el) => { sectionRefs.current[cat.categoryid] = el; }}
            >
              <div className="di-section-title">{cat.categoryname}</div>
              {items.map((item) => {
                const oos = !inStock(item.instock);
                // Find all cart keys that belong to this item
                const cartKeys = Object.keys(cart).filter(k => k.startsWith(`${item.itemid}_`));
                const totalQtyInCart = cartKeys.reduce((s, k) => s + (cart[k]?.qty || 0), 0);
                const simpleKey = makeCartKey(item);
                const dot = vegDot(item.item_attributeid);

                return (
                  <div
                    key={item.itemid}
                    className={`di-item-card ${totalQtyInCart > 0 ? 'in-cart' : ''}`}
                  >
                    {/* Thumb */}
                    <div className="di-item-thumb">
                      {item.item_image_url
                        ? <img src={item.item_image_url} alt={item.itemname} />
                        : '🍽️'}
                    </div>

                    {/* Info */}
                    <div className="di-item-body">
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
                        {dot !== 'other' && (
                          <span className={`di-attr-dot ${dot}`} title={dot === 'veg' ? 'Veg' : (dot === 'egg' ? 'Egg' : 'Non-Veg')} />
                        )}
                        <div className="di-item-name">{item.itemname}</div>
                      </div>
                      {item.itemdescription && (
                        <div className="di-item-desc">{item.itemdescription}</div>
                      )}
                      <div className="di-item-footer">
                        <div className="di-item-price">
                          <span className="di-currency">₹</span>
                          {String(item.itemallowvariation) === '1'
                            ? (() => {
                                const prices = (item.variation || []).filter(v => isActive(v.active)).map(v => parseFloat(v.price));
                                if (prices.length === 0) return '—';
                                const min = Math.min(...prices);
                                const max = Math.max(...prices);
                                return min === max ? min.toFixed(0) : `${min.toFixed(0)}–${max.toFixed(0)}`;
                              })()
                            : parseFloat(item.price || 0).toFixed(0)
                          }
                        </div>

                        {oos ? (
                          <span className="di-oos">Out of Stock</span>
                        ) : totalQtyInCart === 0 ? (
                          <button
                            className="di-add-btn"
                            id={`di-add-${item.itemid}`}
                            onClick={() => setItemDrawer(item)}
                          >
                            + Add
                          </button>
                        ) : String(item.itemallowvariation) === '1' ? (
                          // Variation items: show counter + customize again
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <div className="di-counter">
                              <button onClick={() => {
                                // Remove last added variant or decrement simple
                                const lastKey = cartKeys[cartKeys.length - 1];
                                if (lastKey) handleCartAction('decrement', { cartKey: lastKey, ...cart[lastKey] });
                              }}>−</button>
                              <span>{totalQtyInCart}</span>
                              <button onClick={() => setItemDrawer(item)}>+</button>
                            </div>
                          </div>
                        ) : (
                          <div className="di-counter">
                            <button onClick={() => handleCartAction('decrement', { cartKey: simpleKey, ...cart[simpleKey] })}>−</button>
                            <span>{totalQtyInCart}</span>
                            <button onClick={() => setItemDrawer(item)}>+</button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Bottom padding so FAB doesn't cover last item */}
        <div style={{ height: 80 }} />
      </main>

      {/* ── Item Customization Drawer ── */}
      {itemDrawer && (
        <ItemDrawer
          item={itemDrawer}
          addongroups={addongroups}
          onClose={() => setItemDrawer(null)}
          onAddToCart={addToCart}
        />
      )}

      {/* ── Cart Drawer ── */}
      {cartOpen && (
        <CartDrawer
          cart={cart}
          onClose={() => setCartOpen(false)}
          onPlaceOrder={handleCartAction}
          placing={placing}
        />
      )}

      {/* ── Order error toast ── */}
      {orderError && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--di-red)',
            color: '#fff',
            padding: '12px 20px',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 600,
            maxWidth: 320,
            width: '90%',
            textAlign: 'center',
            zIndex: 300,
            boxShadow: '0 4px 20px rgba(0,0,0,.4)',
          }}
          onClick={() => setOrderError(null)}
        >
          {orderError}
        </div>
      )}
    </div>
  );
}
